/**
 * Panel metadata, colors, and body dispatch — shared by Sidebar and
 * FloatingPanelOverlays so both can render any panel identically.
 */

import type { LibraryItemResponse } from "@poseref/shared";
import {
  Blend,
  BookOpen,
  Camera,
  ChevronDown,
  ChevronRight,
  Clipboard as ClipboardIcon,
  Eye,
  Film,
  FolderOpen,
  Group as GroupIcon,
  Hand,
  Image,
  Layers,
  Layers2,
  Move3d,
  Palette,
  Pencil,
  Pen,
  RefreshCw,
  ScanSearch,
  Spline,
  Upload,
} from "lucide-react";
import type React from "react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { getSingleSelectionTarget } from "../app/objectSelection.ts";
import { getReferenceImageUrl } from "../app/persistence/referenceImageDB.ts";
import { compressAndStoreReferenceImage } from "../app/referenceImageUpload.ts";
import { useStore } from "../app/store.ts";
import type { SnapTarget } from "../app/storeTypes.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import {
  storeKeyToEntityId,
  useEntity,
  useEntityRegistry,
} from "../ecs/index.ts";
import type { ProportionalFalloff } from "../modeling/proportional.ts";
import type { SceneObject } from "../modeling/types.ts";
import { isCurveObject, isLightKind } from "../modeling/types.ts";
import {
  FLOATING_PANEL_IDS,
  type FloatingPanelId,
} from "./floatingPanelStore.ts";
import { HandPoseLibraryPanel } from "./HandPoseLibraryPanel.tsx";
import { InspectorPanelBody } from "./InspectorPanel.tsx";
import { LayerList } from "./LayerList.tsx";
import { MenuItem } from "./menu/MenuItem.tsx";
import { OperatorsPanel } from "./OperatorsPanel.tsx";
import { Popover } from "./Popover.tsx";
import { PoseLibraryPanel } from "./PoseLibraryPanel.tsx";
import { ReferenceLibraryModal } from "./ReferenceLibraryModal.tsx";
import {
  Divider,
  SearchField,
  SectionLabel,
  SegmentedControl,
  ShapeKeyList,
  SliderRow,
  Toggle,
} from "./shared.tsx";
import { EdgeDataPanel } from "./sidebar/EdgeDataPanel.tsx";
import { FaceGroupsPanel } from "./sidebar/FaceGroupsPanel.tsx";
import { InkingFeaturesPanelBody } from "./sidebar/InkingFeaturesPanel.tsx";
import { InkingStrokesPanelBody } from "./sidebar/InkingStrokesPanel.tsx";
import {
  AppearanceSection,
  MaterialSection,
} from "./sidebar/MaterialPanel.tsx";
import { ModifiersPanel } from "./sidebar/ModifiersPanel.tsx";
import { ReferenceSidebarPanel } from "./sidebar/ReferenceSidebarPanel.tsx";
import { RenderCameraPanelBody } from "./sidebar/RenderCameraPanel.tsx";
import { RenderInkingPanelBody } from "./sidebar/RenderInkingPanel.tsx";
import { RenderOutputPanelBody } from "./sidebar/RenderOutputPanel.tsx";
import { SavedInkingsPanelBody } from "./sidebar/SavedInkingsPanel.tsx";
import {
  BlendGroupTransformSection,
  CollectionTransformSection,
  PivotSection,
  TransformSection,
} from "./sidebar/TransformPanel.tsx";
import { VertexGroupsPanel } from "./sidebar/VertexGroupsPanel.tsx";
import {
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Per-panel accent colors — distinct tints for instant recognition.
// ---------------------------------------------------------------------------

export const PANEL_COLORS: Record<
  FloatingPanelId,
  { fg: string; bg: string; bgIdle: string; tint: string }
> = {
  inspector: {
    fg: "#8898a8",
    bg: "rgba(136,152,168,0.30)",
    bgIdle: "rgba(136,152,168,0.18)",
    tint: "rgba(24,32,42,0.65)",
  },
  appearance: {
    fg: "#c090b8",
    bg: "rgba(192,144,184,0.30)",
    bgIdle: "rgba(192,144,184,0.18)",
    tint: "rgba(56,26,46,0.65)",
  },
  transform: {
    fg: "#e8a04c",
    bg: "rgba(232,160,76,0.30)",
    bgIdle: "rgba(232,160,76,0.18)",
    tint: "rgba(72,42,12,0.65)",
  },
  material: {
    fg: "#c876e0",
    bg: "rgba(200,118,224,0.30)",
    bgIdle: "rgba(200,118,224,0.18)",
    tint: "rgba(62,22,72,0.65)",
  },
  modifiers: {
    fg: "#6cc8a0",
    bg: "rgba(108,200,160,0.30)",
    bgIdle: "rgba(108,200,160,0.18)",
    tint: "rgba(16,56,38,0.65)",
  },
  shapekeys: {
    fg: "#68c8c8",
    bg: "rgba(104,200,200,0.30)",
    bgIdle: "rgba(104,200,200,0.18)",
    tint: "rgba(14,56,56,0.65)",
  },
  poseLibrary: {
    fg: "#d8a870",
    bg: "rgba(216,168,112,0.30)",
    bgIdle: "rgba(216,168,112,0.18)",
    tint: "rgba(62,38,16,0.65)",
  },
  handPoses: {
    fg: "#d8c078",
    bg: "rgba(216,192,120,0.30)",
    bgIdle: "rgba(216,192,120,0.18)",
    tint: "rgba(62,52,16,0.65)",
  },
  sketch: {
    fg: "#e09090",
    bg: "rgba(224,144,144,0.30)",
    bgIdle: "rgba(224,144,144,0.18)",
    tint: "rgba(68,24,24,0.65)",
  },
  reference: {
    fg: "#8b9dc3",
    bg: "rgba(139,157,195,0.30)",
    bgIdle: "rgba(139,157,195,0.18)",
    tint: "rgba(22,30,48,0.65)",
  },
  layers: {
    fg: "#b0a8d0",
    bg: "rgba(176,168,208,0.30)",
    bgIdle: "rgba(176,168,208,0.18)",
    tint: "rgba(36,30,56,0.65)",
  },
  renderCamera: {
    fg: "#7aafe0",
    bg: "rgba(122,175,224,0.30)",
    bgIdle: "rgba(122,175,224,0.18)",
    tint: "rgba(18,38,58,0.65)",
  },
  renderInking: {
    fg: "#c0a890",
    bg: "rgba(192,168,144,0.30)",
    bgIdle: "rgba(192,168,144,0.18)",
    tint: "rgba(52,38,26,0.65)",
  },
  renderOutput: {
    fg: "#e8a860",
    bg: "rgba(232,168,96,0.30)",
    bgIdle: "rgba(232,168,96,0.18)",
    tint: "rgba(62,38,12,0.65)",
  },
  vertexGroups: {
    fg: "#78b8d8",
    bg: "rgba(120,184,216,0.30)",
    bgIdle: "rgba(120,184,216,0.18)",
    tint: "rgba(18,44,58,0.65)",
  },
  faceGroups: {
    fg: "#a8c878",
    bg: "rgba(168,200,120,0.30)",
    bgIdle: "rgba(168,200,120,0.18)",
    tint: "rgba(34,48,18,0.65)",
  },
  edgeData: {
    fg: "#e8a060",
    bg: "rgba(232,160,96,0.30)",
    bgIdle: "rgba(232,160,96,0.18)",
    tint: "rgba(62,38,14,0.65)",
  },
  inkingFeatures: {
    fg: "#b0a088",
    bg: "rgba(176,160,136,0.30)",
    bgIdle: "rgba(176,160,136,0.18)",
    tint: "rgba(44,36,26,0.65)",
  },
  inkingStrokes: {
    fg: "#a0b8c0",
    bg: "rgba(160,184,192,0.30)",
    bgIdle: "rgba(160,184,192,0.18)",
    tint: "rgba(26,38,42,0.65)",
  },
  inkingSaved: {
    fg: "#9cc1a8",
    bg: "rgba(156,193,168,0.30)",
    bgIdle: "rgba(156,193,168,0.18)",
    tint: "rgba(22,42,32,0.65)",
  },
};

// ---------------------------------------------------------------------------
// Per-panel badges — optional status indicators shown on collapsed sidebar icons.
// ---------------------------------------------------------------------------

/** Shows the selected object's albedo as a small color dot on the Material icon. */
function MaterialBadge() {
  const color = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target || target.kind !== "sceneObject") return null;
    const obj = s.sceneObjects.get(target.id);
    // Lights use the Inspector panel, not Material — never show a color here for them.
    if (!obj || obj.light != null) return null;
    const primary = obj.materialSlots[0]?.materialId;
    if (!primary) return null;
    return s.materials.get(primary)?.properties.color ?? null;
  });
  if (!color) return null;
  return (
    <span
      style={{
        position: "absolute",
        bottom: 4,
        right: 4,
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        border: "1px solid rgba(0,0,0,0.45)",
        pointerEvents: "none",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-panel metadata — icon, label, tip, default floating position, width.
// ---------------------------------------------------------------------------

export const PANEL_META: Record<
  FloatingPanelId,
  {
    icon: ReactNode;
    label: string;
    tip: string;
    defaultPosition: { top?: number; right?: number; left?: number };
    width: number;
    /** Optional badge rendered over the icon in the collapsed sidebar strip. */
    Badge?: React.ComponentType;
  }
> = {
  inspector: {
    icon: <ScanSearch size={13} />,
    label: "Inspector",
    tip: "Object Inspector",
    defaultPosition: { top: 94, left: 16 },
    width: 340,
  },
  appearance: {
    icon: <Eye size={13} />,
    label: "Appearance",
    tip: "Viewport display & render visibility",
    defaultPosition: { top: 100, right: 60 },
    width: 340,
  },
  transform: {
    icon: <Move3d size={13} />,
    label: "Transform",
    tip: "Transform",
    defaultPosition: { top: 100, left: 60 },
    width: 340,
  },
  material: {
    icon: <Palette size={13} />,
    label: "Material",
    tip: "Material",
    defaultPosition: { top: 100, right: 60 },
    width: 340,
    Badge: MaterialBadge,
  },
  modifiers: {
    icon: <Layers size={13} />,
    label: "Modifiers",
    tip: "Modifiers",
    defaultPosition: { top: 200, left: 60 },
    width: 340,
  },
  shapekeys: {
    icon: <Blend size={13} />,
    label: "Shape Keys",
    tip: "Shape Keys",
    defaultPosition: { top: 300, left: 60 },
    width: 340,
  },
  poseLibrary: {
    icon: <BookOpen size={13} />,
    label: "Pose Library",
    tip: "Pose Library",
    defaultPosition: { top: 100, left: 60 },
    width: 340,
  },
  handPoses: {
    icon: <Hand size={13} />,
    label: "Hand Poses",
    tip: "Hand Poses",
    defaultPosition: { top: 200, left: 60 },
    width: 340,
  },
  sketch: {
    icon: <Pencil size={13} />,
    label: "Sketch",
    tip: "Sketch Tools",
    defaultPosition: { top: 100, left: 60 },
    width: 340,
  },
  reference: {
    icon: <Image size={13} />,
    label: "Reference",
    tip: "Reference Image Layers",
    defaultPosition: { top: 100, left: 60 },
    width: 340,
  },
  layers: {
    icon: <Layers2 size={13} />,
    label: "Layers",
    tip: "Layers",
    defaultPosition: { top: 200, left: 60 },
    width: 340,
  },
  renderCamera: {
    icon: <Camera size={13} />,
    label: "Camera",
    tip: "Camera, output dimensions & saved views",
    defaultPosition: { top: 100, left: 60 },
    width: 340,
  },
  renderInking: {
    icon: <Pen size={13} />,
    label: "Inking",
    tip: "Inking outline settings",
    defaultPosition: { top: 200, left: 60 },
    width: 340,
  },
  renderOutput: {
    icon: <Film size={13} />,
    label: "Render",
    tip: "Render output & export",
    defaultPosition: { top: 300, left: 60 },
    width: 340,
  },
  vertexGroups: {
    icon: <GroupIcon size={13} />,
    label: "Vertex Groups",
    tip: "Named sets of vertices for modifiers and selection",
    defaultPosition: { top: 300, left: 60 },
    width: 340,
  },
  faceGroups: {
    icon: <GroupIcon size={13} />,
    label: "Face Groups",
    tip: "Saved named face selections",
    defaultPosition: { top: 320, left: 60 },
    width: 340,
  },
  edgeData: {
    icon: <Spline size={13} />,
    label: "Edge Data",
    tip: "Mark UV seams and sharp edges on the current selection",
    defaultPosition: { top: 340, left: 60 },
    width: 340,
  },
  inkingFeatures: {
    icon: <Layers size={13} />,
    label: "Features",
    tip: "Feature extraction layers & sensitivity",
    defaultPosition: { top: 100, left: 60 },
    width: 340,
  },
  inkingStrokes: {
    icon: <Pen size={13} />,
    label: "Strokes",
    tip: "Stroke construction & curve fitting settings",
    defaultPosition: { top: 200, left: 60 },
    width: 340,
  },
  inkingSaved: {
    icon: <BookOpen size={13} />,
    label: "Saved Inkings",
    tip: "Save the current inking & camera view; tap a saved entry to reload it",
    defaultPosition: { top: 300, left: 60 },
    width: 340,
  },
};

// ---------------------------------------------------------------------------
// Per-panel applicability — pure logic that mirrors each panel body's own
// NoSelection guard. Used by the sidebar/floating-panel containers to hide
// panels entirely (header included) instead of showing a placeholder.
//
// Reading directly from SceneObject.objectKind avoids a full ECS rebuild.
// ---------------------------------------------------------------------------

function isPanelApplicable(
  panelId: FloatingPanelId,
  hasAnySelection: boolean,
  obj: SceneObject | undefined,
  hasEditTarget: boolean,
  isCurveEdit: boolean,
): boolean {
  switch (panelId) {
    case "inspector":
    case "transform":
    case "shapekeys":
      // Any selection is sufficient; the body handles per-type details.
      return hasAnySelection;
    case "appearance":
      // AppearancePanelBody: needs a single scene object (any kind — including lights for render-visibility toggling).
      return obj !== undefined;
    case "material":
      // MaterialPanelBody: needs a scene object that isn't a light.
      return obj !== undefined && !isLightKind(obj.objectKind);
    case "modifiers":
      // ModifiersPanelBody: needs a scene object that isn't a light, mannequin, or curve.
      return (
        obj !== undefined &&
        !isLightKind(obj.objectKind) &&
        obj.objectKind !== "mannequin" &&
        !isCurveObject(obj)
      );
    case "vertexGroups":
      // VertexGroupsPanelBody: edit-mode-only and mesh-only.
      return hasEditTarget && obj !== undefined && !isCurveEdit;
    case "faceGroups":
      // FaceGroupsPanelBody: edit-mode-only and mesh-only.
      return hasEditTarget && obj !== undefined && !isCurveEdit;
    case "edgeData":
      // EdgeDataPanelBody: edit-mode-only and mesh-only.
      return hasEditTarget && obj !== undefined && !isCurveEdit;
    default:
      return true;
  }
}

/**
 * Returns a per-panel boolean record indicating whether each panel should be
 * visible given the current selection. Call once per container component;
 * the result is memoized against selection state.
 */
export function usePanelApplicabilities(): Record<FloatingPanelId, boolean> {
  const hasAnySelection = useStore((s) => s.selectedObjectTargets.length > 0);
  const editMode = useStore((s) => s.editMode);
  const obj = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    return target?.kind === "sceneObject"
      ? s.sceneObjects.get(target.id)
      : undefined;
  });

  return useMemo(() => {
    const result = {} as Record<FloatingPanelId, boolean>;
    for (const id of FLOATING_PANEL_IDS) {
      result[id] = isPanelApplicable(
        id,
        hasAnySelection,
        obj,
        editMode !== null,
        editMode?.isCurve ?? false,
      );
    }
    return result;
  }, [hasAnySelection, obj, editMode]);
}

// ---------------------------------------------------------------------------
// Panel body dispatch — renders the correct content for a given panel id.
// ---------------------------------------------------------------------------

export function PanelBody({
  panelId,
  mode,
}: {
  panelId: FloatingPanelId;
  mode: string;
}) {
  switch (panelId) {
    case "inspector":
      return <InspectorPanelBody />;
    case "appearance":
      return <AppearancePanelBody />;
    case "material":
      return <MaterialPanelBody />;
    case "transform":
      return <TransformPanelBody mode={mode} />;
    case "modifiers":
      return <ModifiersPanelBody />;
    case "shapekeys":
      return <ShapeKeysPanelBody />;
    case "poseLibrary":
      return <PoseLibraryPanelBody />;
    case "handPoses":
      return <HandPosesPanelBody />;
    case "sketch":
      return <SketchPanelBody />;
    case "reference":
      return <ReferenceSidebarPanelBody />;
    case "layers":
      return <LayersPanelBody />;
    case "renderCamera":
      return <RenderCameraPanelBody />;
    case "renderInking":
      return <RenderInkingPanelBody />;
    case "renderOutput":
      return <RenderOutputPanelBody />;
    case "vertexGroups":
      return <VertexGroupsPanelBody />;
    case "faceGroups":
      return <FaceGroupsPanelBody />;
    case "edgeData":
      return <EdgeDataPanelBody />;
    case "inkingFeatures":
      return <InkingFeaturesPanelBody />;
    case "inkingStrokes":
      return <InkingStrokesPanelBody />;
    case "inkingSaved":
      return <SavedInkingsPanelBody />;
  }
}

// ---------------------------------------------------------------------------
// No-selection placeholder
// ---------------------------------------------------------------------------

function NoSelection() {
  return (
    <div
      style={{
        fontSize: FONT_SIZE_BODY,
        color: COLOR_TEXT_DISABLED,
        padding: "4px 0",
      }}
    >
      No object selected
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual panel bodies
// ---------------------------------------------------------------------------

function MaterialPanelBody() {
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const selectedId =
    selectedTarget?.kind === "sceneObject" ? selectedTarget.id : null;
  const entity = useEntity(selectedId ? storeKeyToEntityId(selectedId) : null);

  if (
    !entity ||
    entity.origin !== "scene" ||
    entity.kind === "light" ||
    entity.kind === "image_plane"
  )
    return <NoSelection />;

  return <MaterialSection objectId={selectedId!} bare />;
}

function ModifiersPanelBody() {
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const selectedId =
    selectedTarget?.kind === "sceneObject" ? selectedTarget.id : null;
  const entity = useEntity(selectedId ? storeKeyToEntityId(selectedId) : null);

  if (
    !entity ||
    entity.kind === "light" ||
    entity.kind === "mannequin" ||
    entity.kind === "curve" ||
    entity.kind === "image_plane"
  )
    return <NoSelection />;

  return (
    <ModifiersPanel
      objectName={selectedId!}
      baseModifiers={[
        ...(entity.components.modifierStack?.baseModifiers ?? []),
      ]}
      bare
      isSceneObject={entity.origin === "scene"}
    />
  );
}

function VertexGroupsPanelBody() {
  return <VertexGroupsPanel bare />;
}

function FaceGroupsPanelBody() {
  return <FaceGroupsPanel bare />;
}

function EdgeDataPanelBody() {
  return <EdgeDataPanel bare />;
}

function TransformPanelBody({ mode }: { mode: string }) {
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );

  if (mode === "object" && !selectedTarget) return <NoSelection />;
  if (!selectedTarget) return <NoSelection />;

  switch (selectedTarget.kind) {
    case "blendEntry":
      return <BlendGroupTransformSection entryId={selectedTarget.id} bare />;
    case "collection":
      return (
        <CollectionTransformSection collectionId={selectedTarget.id} bare />
      );
    case "sceneObject":
      return <TransformSection objectId={selectedTarget.id} bare />;
  }
}

const SNAP_TARGET_OPTIONS: { label: string; value: SnapTarget }[] = [
  { label: "Grid", value: "grid" },
  { label: "Vertex", value: "vertex" },
  { label: "Edge", value: "edge" },
  { label: "Face", value: "face" },
  { label: "Surface", value: "surface" },
  { label: "Object", value: "object" },
];

const FALLOFF_OPTIONS: { label: string; value: ProportionalFalloff }[] = [
  { label: "Smooth", value: "smooth" },
  { label: "Linear", value: "linear" },
  { label: "Sharp", value: "sharp" },
  { label: "Constant", value: "constant" },
];

export function OptionsPanelBody() {
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const selectedId =
    selectedTarget?.kind === "sceneObject" ? selectedTarget.id : null;
  const entity = useEntity(selectedId ? storeKeyToEntityId(selectedId) : null);
  const sceneObjectId = entity?.origin === "scene" ? selectedId! : undefined;

  const snapEnabled = useStore((s) => s.snapEnabled);
  const setSnapEnabled = useStore((s) => s.setSnapEnabled);
  const snapTarget = useStore((s) => s.snapTarget);
  const setSnapTarget = useStore((s) => s.setSnapTarget);
  const snapWithRotation = useStore((s) => s.snapWithRotation);
  const setSnapWithRotation = useStore((s) => s.setSnapWithRotation);
  const surfaceProjectIndividual = useStore((s) => s.surfaceProjectIndividual);
  const setSurfaceProjectIndividual = useStore(
    (s) => s.setSurfaceProjectIndividual,
  );

  const proportionalEnabled = useStore((s) => s.proportionalEnabled);
  const setProportionalEnabled = useStore((s) => s.setProportionalEnabled);
  const proportionalRadius = useStore((s) => s.proportionalRadius);
  const setProportionalRadius = useStore((s) => s.setProportionalRadius);
  const proportionalFalloff = useStore((s) => s.proportionalFalloff);
  const setProportionalFalloff = useStore((s) => s.setProportionalFalloff);

  const mode = useStore((s) => s.mode);
  const showProportional = mode === "edit";

  return (
    <>
      <SectionLabel>Snapping</SectionLabel>
      <Toggle
        label="Enable Snapping"
        checked={snapEnabled}
        onChange={setSnapEnabled}
      />
      {snapEnabled && (
        <>
          <SegmentedControl
            options={SNAP_TARGET_OPTIONS}
            value={snapTarget}
            onChange={setSnapTarget}
          />
          <div style={{ marginTop: 6 }}>
            <Toggle
              label="Snap with Rotation"
              checked={snapWithRotation}
              onChange={setSnapWithRotation}
            />
          </div>
          {showProportional && snapTarget === "surface" && (
            <div style={{ marginTop: 6 }}>
              <Toggle
                label="Project Individual Elements"
                checked={surfaceProjectIndividual}
                onChange={setSurfaceProjectIndividual}
              />
            </div>
          )}
        </>
      )}

      {showProportional && (
        <>
          <Divider />
          <SectionLabel>Proportional Editing</SectionLabel>
          <Toggle
            label="Enable"
            checked={proportionalEnabled}
            onChange={setProportionalEnabled}
          />
          {proportionalEnabled && (
            <>
              <SliderRow
                label="Radius"
                value={proportionalRadius}
                min={1}
                max={500}
                displayValue={`${proportionalRadius}px`}
                onChange={setProportionalRadius}
              />
              <SegmentedControl
                options={FALLOFF_OPTIONS}
                value={proportionalFalloff}
                onChange={setProportionalFalloff}
              />
            </>
          )}
        </>
      )}

      <Divider />
      <PivotSection objectId={sceneObjectId} bare />
    </>
  );
}

function AppearancePanelBody() {
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const selectedId =
    selectedTarget?.kind === "sceneObject" ? selectedTarget.id : null;
  const entity = useEntity(selectedId ? storeKeyToEntityId(selectedId) : null);

  if (!entity) return <NoSelection />;

  return (
    <AppearanceSection
      objectId={selectedId!}
      isSceneObject={entity.origin === "scene"}
      bare
    />
  );
}

// ---------------------------------------------------------------------------
// Shape Keys panel body
// ---------------------------------------------------------------------------

type ShapeKeyEntry = {
  name: string;
  setName: string;
  keys: {
    name: string;
    index: number;
    min: number;
    max: number;
    defaultValue: number;
  }[];
};

function ShapeKeysPanelBody() {
  const selectedObjects = useSelectedLeafObjectIds();
  const blendFiles = useStore((s) => s.blendFiles);
  const registry = useEntityRegistry();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const filter = search.toLowerCase();

  const driverGraph = useStore((s) => s.driverGraph);

  const objectsWithKeys = useMemo(() => {
    const result: ShapeKeyEntry[] = [];
    for (const selectedId of selectedObjects) {
      const entity = registry.get(storeKeyToEntityId(selectedId));
      const sk = entity?.components.shapeKey;
      if (!sk) continue;
      let skSet: { name: string; keys: ShapeKeyEntry["keys"] } | undefined;
      for (const entry of blendFiles.values()) {
        const found = entry.file.shapeKeySets.get(sk.shapeKeySetRef);
        if (found) {
          skSet = found;
          break;
        }
      }
      if (!skSet || skSet.keys.length <= 1) continue;

      const driven =
        driverGraph?.getDrivenKeysForSet(skSet.name) ?? new Set<string>();
      const editableCount = skSet.keys.filter(
        (k) => k.index > 0 && !driven.has(k.name),
      ).length;
      if (editableCount === 0) continue;

      result.push({ name: selectedId, setName: skSet.name, keys: skSet.keys });
    }
    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [selectedObjects, blendFiles, registry, driverGraph]);

  if (selectedObjects.size === 0) return <NoSelection />;

  if (objectsWithKeys.length === 0) {
    return (
      <div
        style={{
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_DISABLED,
          padding: "4px 0",
        }}
      >
        No shape keys
      </div>
    );
  }

  const toggleExpanded = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const resetObject = (obj: ShapeKeyEntry) => {
    const s = useStore.getState();
    s.pushUndo();
    const weights = new Map(s.shapeKeyWeights);
    const kw = new Map<string, number>();
    for (const key of obj.keys) kw.set(key.name, key.defaultValue);
    weights.set(obj.setName, kw);
    useStore.setState({ shapeKeyWeights: weights });
  };

  return (
    <div style={{ maxHeight: 400, overflowY: "auto" }}>
      <div style={{ padding: "0 0 8px" }}>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Search keys…"
        />
      </div>

      {objectsWithKeys.map((obj) => {
        const hasMatch =
          filter &&
          obj.keys.some(
            (k) => k.index > 0 && k.name.toLowerCase().includes(filter),
          );
        const isExpanded = expanded.has(obj.name) || !!hasMatch;

        return (
          <ShapeKeyFoldout
            key={obj.name}
            obj={obj}
            isExpanded={isExpanded}
            filter={filter}
            onToggle={() => toggleExpanded(obj.name)}
            onReset={() => resetObject(obj)}
          />
        );
      })}
    </div>
  );
}

function ShapeKeyFoldout({
  obj,
  isExpanded,
  filter,
  onToggle,
  onReset,
}: {
  obj: ShapeKeyEntry;
  isExpanded: boolean;
  filter: string;
  onToggle: () => void;
  onReset: () => void;
}) {
  const visibleKeyCount = useMemo(() => {
    const driverGraph = useStore.getState().driverGraph;
    const driven =
      driverGraph?.getDrivenKeysForSet(obj.setName) ?? new Set<string>();
    return obj.keys.filter(
      (k) =>
        k.index > 0 &&
        !driven.has(k.name) &&
        (!filter || k.name.toLowerCase().includes(filter)),
    ).length;
  }, [obj, filter]);

  return (
    <div>
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: COLOR_TEXT_FAINT,
            flexShrink: 0,
          }}
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_SECONDARY,
          }}
        >
          {obj.name}
        </span>
        <span
          style={{
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_FAINT,
            flexShrink: 0,
          }}
        >
          {visibleKeyCount}
        </span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            onReset();
          }}
          title={`Reset ${obj.name}`}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "2px 4px",
            cursor: "pointer",
            flexShrink: 0,
            color: COLOR_TEXT_FAINT,
          }}
        >
          <RefreshCw size={12} />
        </span>
      </div>
      {isExpanded && (
        <ShapeKeyList setName={obj.setName} keys={obj.keys} filter={filter} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simple wrapper bodies
// ---------------------------------------------------------------------------

export function OperatorsPanelBody() {
  return <OperatorsPanel />;
}

function PoseLibraryPanelBody() {
  const objectId = useMannequinObjectId();
  if (!objectId) return <NoSelection />;
  return <PoseLibraryPanel objectId={objectId} />;
}

function HandPosesPanelBody() {
  const objectId = useMannequinObjectId();
  if (!objectId) return <NoSelection />;
  return <HandPoseLibraryPanel objectId={objectId} />;
}

export function useMannequinObjectId(): string | null {
  const poseMode = useStore((s) => s.poseMode);
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const selectedId =
    selectedTarget?.kind === "sceneObject" ? selectedTarget.id : null;
  const entity = useEntity(selectedId ? storeKeyToEntityId(selectedId) : null);

  if (poseMode) return poseMode.objectId;
  if (entity?.kind === "mannequin" && entity.origin === "scene")
    return selectedId;
  return null;
}

function SketchPanelBody() {
  // All sketch controls are now in the tool palette.
  return null;
}

function ReferenceSidebarPanelBody() {
  return <ReferenceSidebarPanel bare />;
}

function LayersPanelBody() {
  const mode = useStore((s) => s.workspaceMode);
  switch (mode) {
    case "sketch":
      return <SketchLayersBody />;
    case "reference":
      return <ReferenceLayersBody />;
    default:
      return null;
  }
}

function SketchLayersBody() {
  const sketchMode = useStore((s) => s.sketchMode);
  const layers = useStore((s) => s.sketch.layers);
  const addSketchLayer = useStore((s) => s.addSketchLayer);
  const deleteSketchLayer = useStore((s) => s.deleteSketchLayer);
  const clearSketchLayer = useStore((s) => s.clearSketchLayer);
  const setActiveSketchLayer = useStore((s) => s.setActiveSketchLayer);
  const setSketchLayerOpacity = useStore((s) => s.setSketchLayerOpacity);
  const setSketchLayerVisible = useStore((s) => s.setSketchLayerVisible);
  const beginSketchLayerStrokeColorEdit = useStore(
    (s) => s.beginSketchLayerStrokeColorEdit,
  );
  const setSketchLayerStrokeColor = useStore(
    (s) => s.setSketchLayerStrokeColor,
  );
  const endSketchLayerStrokeColorEdit = useStore(
    (s) => s.endSketchLayerStrokeColorEdit,
  );
  const moveSketchLayer = useStore((s) => s.moveSketchLayer);
  const renameSketchLayer = useStore((s) => s.renameSketchLayer);

  if (!sketchMode) return null;

  return (
    <LayerList
      layers={[...layers].reverse()}
      activeLayerId={sketchMode.activeLayerId}
      onSelectLayer={setActiveSketchLayer}
      onSetVisible={setSketchLayerVisible}
      onSetOpacity={setSketchLayerOpacity}
      onAdd={addSketchLayer}
      onClear={clearSketchLayer}
      onDelete={deleteSketchLayer}
      onRename={renameSketchLayer}
      getLayerColor={(layer) =>
        layer.strokes[layer.strokes.length - 1]?.color ?? sketchMode.color
      }
      onBeginLayerColorEdit={beginSketchLayerStrokeColorEdit}
      onSetLayerColor={setSketchLayerStrokeColor}
      onEndLayerColorEdit={endSketchLayerStrokeColorEdit}
      isLayerColorDisabled={(layer) => layer.strokes.length === 0}
      onMove={(id, dir) => moveSketchLayer(id, dir)}
      canDelete={(ls) => ls.length > 1}
    />
  );
}

function ReferenceLayersBody() {
  const referenceLayers = useStore((s) => s.referenceLayers);
  const activeReferenceLayerId = useStore((s) => s.activeReferenceLayerId);
  const deleteReferenceLayer = useStore((s) => s.deleteReferenceLayer);
  const setActiveReferenceLayer = useStore((s) => s.setActiveReferenceLayer);
  const setRefLayerOpacity = useStore((s) => s.setRefLayerOpacity);
  const setRefLayerVisible = useStore((s) => s.setRefLayerVisible);
  const moveReferenceLayer = useStore((s) => s.moveReferenceLayer);
  const setRefLayerName = useStore((s) => s.setRefLayerName);
  const addReferenceLayer = useStore((s) => s.addReferenceLayer);

  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFromFile = useCallback(() => {
    setAddMenuOpen(false);
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      try {
        const { imageKey, imageUrl } = await compressAndStoreReferenceImage(
          file,
          file.name.replace(/\.[^.]+$/, ""),
        );
        addReferenceLayer(imageKey, imageUrl);
      } catch (err) {
        console.error("Failed to upload reference image:", err);
      }
    },
    [addReferenceLayer],
  );

  const handleAddFromLibrary = useCallback(() => {
    setAddMenuOpen(false);
    setLibraryModalOpen(true);
  }, []);

  const handleImportFromLibrary = useCallback(
    async (item: LibraryItemResponse) => {
      setLibraryModalOpen(false);
      try {
        if (!item.sourceHash) return;
        const imageUrl = await getReferenceImageUrl(item.sourceHash);
        if (!imageUrl) return;
        addReferenceLayer(item.sourceHash, imageUrl, item.name);
      } catch (err) {
        console.error("Failed to import reference from library:", err);
      }
    },
    [addReferenceLayer],
  );

  const handlePaste = useCallback(async () => {
    setAddMenuOpen(false);
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const { imageKey, imageUrl } = await compressAndStoreReferenceImage(
            blob,
            "Pasted image",
          );
          addReferenceLayer(imageKey, imageUrl);
          return;
        }
      }
    } catch {
      // clipboard access denied or empty
    }
  }, [addReferenceLayer]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        style={{ display: "none" }}
      />
      <ReferenceLibraryModal
        open={libraryModalOpen}
        onClose={() => setLibraryModalOpen(false)}
        onSelect={handleImportFromLibrary}
      />
      <LayerList
        layers={[...referenceLayers].reverse()}
        activeLayerId={activeReferenceLayerId}
        onSelectLayer={setActiveReferenceLayer}
        onSetVisible={(id, v) => setRefLayerVisible(id, v)}
        onSetOpacity={(id, v) => setRefLayerOpacity(id, v)}
        onMove={(id, dir) => moveReferenceLayer(id, dir)}
        onRename={setRefLayerName}
        onAdd={() => setAddMenuOpen((v) => !v)}
        addButtonRef={addButtonRef}
        onDelete={deleteReferenceLayer}
        opacityDefault={0.3}
        emptyMessage="No reference images"
      />
      <Popover
        anchorRef={addButtonRef}
        open={addMenuOpen}
        onClose={() => setAddMenuOpen(false)}
      >
        <MenuItem
          label="From File…"
          icon={<Upload size={14} />}
          onClick={handleAddFromFile}
        />
        <MenuItem
          label="From Library"
          icon={<FolderOpen size={14} />}
          onClick={handleAddFromLibrary}
        />
        <MenuItem
          label="Paste from Clipboard"
          icon={<ClipboardIcon size={14} />}
          onClick={handlePaste}
        />
      </Popover>
    </>
  );
}
